import {
    FieldColumns,
    FormField,
    FormFieldGrid,
    type FieldConfig,
    type FieldEventHandlers
} from './form-field';
import { Box, SxProps } from '@mui/material';
import { type CategorizedOption, type Option } from '../input';
import { useMemo, useCallback } from 'react';

export type FormFieldsConfig = {
    fields: FieldConfig[];
    columns?: FieldColumns;
    sx?: SxProps;
};

export interface FormFieldsProps<T extends Record<string, unknown> = Record<string, unknown>> {
    configs: FormFieldsConfig[];
    readOnly?: boolean;
    values?: T;
    eventHandlers?: FieldEventHandlers;
    errors?: Record<string, string>;
    fieldOptions?: Record<string, Option[]>;
    categorizedFieldOptions?: Record<string, CategorizedOption[]>;
    fieldLoadingOptions?: Record<string, boolean>;
}

const COLUMN_BREAKPOINTS = {
    SINGLE: 1,
    DOUBLE: 2,
    TRIPLE: 3
} as const;

const getVisibleFields = <T extends Record<string, unknown>>(
    fields: FieldConfig[],
    values?: T
): FieldConfig[] => {
    const shouldRenderField = (field: FieldConfig): boolean => {
        if (!field.dependsOn || !values) return true;

        return field.dependsOn.every(dependency => {
            const dependencyValue = values[dependency.name];
            return (
                typeof dependencyValue === 'string' && dependency.value.includes(dependencyValue)
            );
        });
    };

    return fields.filter(shouldRenderField);
};

const calculateColumns = (visibleFields: FieldConfig[]): FieldColumns => {
    const fieldColumnRequirements = visibleFields
        .map(field => field.columns)
        .filter(Boolean) as FieldColumns[];

    if (fieldColumnRequirements.length > 0) {
        return Math.max(...fieldColumnRequirements) as FieldColumns;
    }

    const count = visibleFields.length;
    switch (count) {
        case COLUMN_BREAKPOINTS.SINGLE:
            return COLUMN_BREAKPOINTS.SINGLE;
        case COLUMN_BREAKPOINTS.DOUBLE:
            return COLUMN_BREAKPOINTS.DOUBLE;
        default:
            return count >= COLUMN_BREAKPOINTS.TRIPLE
                ? COLUMN_BREAKPOINTS.TRIPLE
                : COLUMN_BREAKPOINTS.SINGLE;
    }
};

const enhanceFieldConfig = <T extends Record<string, unknown>>(
    field: FieldConfig,
    values?: T,
    readOnly?: boolean,
    errors?: Record<string, string>,
    fieldOptions?: Record<string, Option[]>,
    categorizedFieldOptions?: Record<string, CategorizedOption[]>,
    fieldLoadingOptions?: Record<string, boolean>
) => ({
    ...field,
    options: fieldOptions?.[field.name] || field.options,
    categorizedOptions: categorizedFieldOptions?.[field.name] || field.categorizedOptions,
    readOnly,
    value: values?.[field.name],
    checked: values?.[field.name],
    error: errors?.[field.name],
    helperText: errors?.[field.name],
    loading: fieldLoadingOptions?.[field.name]
});

export const FormFields = <T extends Record<string, unknown> = Record<string, unknown>>({
    configs,
    readOnly = false,
    values,
    eventHandlers,
    errors,
    fieldOptions,
    categorizedFieldOptions,
    fieldLoadingOptions
}: FormFieldsProps<T>) => {
    const configsWithCalculations = useMemo(() => {
        return configs.map(config => {
            const visibleFields = getVisibleFields(config.fields, values);
            const columns = calculateColumns(visibleFields);

            return {
                ...config,
                visibleFields,
                columns
            };
        });
    }, [configs, values]);

    const renderFormField = useCallback(
        (field: FieldConfig) => {
            const enhancedConfig = enhanceFieldConfig(
                field,
                values,
                readOnly,
                errors,
                fieldOptions,
                categorizedFieldOptions,
                fieldLoadingOptions
            );

            return (
                <FormField key={field.name} config={enhancedConfig} eventHandlers={eventHandlers} />
            );
        },
        [
            values,
            readOnly,
            errors,
            fieldOptions,
            categorizedFieldOptions,
            fieldLoadingOptions,
            eventHandlers
        ]
    );

    return (
        <Box
            className={readOnly ? 'form-fields-readonly' : ''}
            sx={{ display: 'flex', flexDirection: 'column' }}
        >
            {configsWithCalculations.map((config, index) => (
                <FormFieldGrid key={index} columns={config.columns} sx={config.sx}>
                    {config.visibleFields.map(renderFormField)}
                </FormFieldGrid>
            ))}
        </Box>
    );
};
